import { FhirBundleSdk } from "@metriport/fhir-sdk";
import { useState } from "react";
import { HStack, Switch, Text } from "@chakra-ui/react-v3";
import { TableRow } from "../../utils/tables-metadata";
import { LabRowData } from "./lab-rows";
import { IndividualLabTable } from "./individual-lab-table";
import { PanelLabTable } from "./panel-lab-table";

type ViewMode = "individual" | "panel";

export type LabTableProps = {
  rows: TableRow[];
  onRowClick: (row: TableRow) => void;
  fhirSdk: FhirBundleSdk;
  filterQuery: string;
  onPdfView?: (docId: string) => void;
  displayName?: string;
  isRowViewed?: (row: TableRow) => boolean;
};

/**
 * LabTable with Individual/Panel toggle.
 * Used in browse mode only. In search mode, IndividualLabTable and PanelLabTable
 * are rendered as separate sections via SearchTableContent.
 */
export function LabTable({
  rows,
  onRowClick,
  fhirSdk,
  filterQuery,
  displayName = "Labs",
  isRowViewed,
}: LabTableProps) {
  const [viewMode, setViewMode] = useState<ViewMode>("individual");

  // Toggle always shows in browse mode (this component is only used in browse mode)
  const toggleUI = (
    <HStack gap={3} px={4} py={2} borderRadius="full" borderWidth="1px" borderColor="border">
      <Text fontSize="sm" fontWeight="medium" color="fg.muted">
        Individual
      </Text>
      <Switch.Root
        checked={viewMode === "panel"}
        onCheckedChange={() => setViewMode(v => (v === "individual" ? "panel" : "individual"))}
        colorPalette="blue"
      >
        <Switch.HiddenInput />
        <Switch.Control />
      </Switch.Root>
      <Text fontSize="sm" fontWeight="medium" color="fg.muted">
        Panels
      </Text>
    </HStack>
  );

  if (viewMode === "panel") {
    return (
      <PanelLabTable
        fhirSdk={fhirSdk}
        onRowClick={onRowClick}
        filterQuery={filterQuery}
        displayName={displayName}
        isRowViewed={isRowViewed}
        headerActionsSlot={toggleUI}
      />
    );
  }

  return (
    <IndividualLabTable
      rows={rows as LabRowData[]}
      onRowClick={onRowClick}
      filterQuery={filterQuery}
      displayName={displayName}
      isRowViewed={isRowViewed}
      headerActionsSlot={toggleUI}
    />
  );
}
